import React, { lazy, Suspense, useMemo, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { App as AntdApp, ConfigProvider } from 'antd';
import koKR from 'antd/locale/ko_KR';
import { CommonCodeProvider } from './context/CommonCodeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { MenuProvider } from './context/MenuContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { buildAntdTheme } from './lib/antdTheme';
import MainLayout from './components/layout/MainLayout';
import appRoutes from './routes';
import { goToPlatformLogin } from './lib/platformAuth';

/*
 * 로그인 화면은 더 이상 없다.
 *
 * WORK&Hub SSO 로 바꾸면서 계정·비밀번호를 CRM 이 다루지 않는다. 예전
 * LoginPage · MagicLoginPage 는 사라진 /api/auth/login · /api/auth/magic-login
 * 을 부르므로, 남겨 두면 "눌러도 아무 일도 안 나는 화면" 이 된다.
 * 그 경로로 들어오면 플랫폼 로그인으로 보낸다.
 */
const GoToPlatformLogin = () => {
  useEffect(() => { goToPlatformLogin(); }, []);
  return <FullScreenLoader message="WORK&Hub 로그인으로 이동합니다..." />;
};

const FullScreenLoader = ({ message }) => (
  <div className="flex h-screen w-screen items-center justify-center bg-gray-50">
    <div className="flex flex-col items-center gap-4">
      <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      {message && <p className="text-sm font-bold text-gray-500">{message}</p>}
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <FullScreenLoader message="보안 세션을 확인하는 중..." />;
  if (!isAuthenticated) return <GoToPlatformLogin />;

  return children;
};

/**
 * 선택한 테마를 antd 토큰으로 넘긴다.
 * ThemeProvider 안쪽이어야 useTheme 을 쓸 수 있어 컴포넌트를 따로 뒀다.
 */
function ThemedApp() {
  const { theme } = useTheme();
  const antdConfig = useMemo(() => buildAntdTheme(theme), [theme]);

  return (
    // antd 의 App 컨텍스트를 깔아두면 message/modal 이 테마·로케일을 그대로 따른다.
    <ConfigProvider locale={koKR} theme={antdConfig}>
      <AntdApp>
        <AppRoutes />
      </AntdApp>
    </ConfigProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ThemedApp />
    </ThemeProvider>
  );
}

function AppRoutes() {
  return (
    <AuthProvider>
      <CommonCodeProvider>
        <BrowserRouter>
          {/* MenuProvider 는 useLocation 을 사용하므로 BrowserRouter 안쪽에 있어야 한다 */}
          <MenuProvider>
            <Suspense fallback={<FullScreenLoader />}>
              <Routes>
                {/* 공용 라우트 */}
                <Route path="/login" element={<GoToPlatformLogin />} />
                <Route path="/magic-login" element={<GoToPlatformLogin />} />

                {/* 인증 필요 라우트 */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                >
                  {appRoutes}
                </Route>

                {/* 잘못된 경로 처리 */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </MenuProvider>
        </BrowserRouter>
      </CommonCodeProvider>
    </AuthProvider>
  );
}

export default App;
